import { generateUrlFromIndexUiState, parseUrlToIndexUiState, type IndexUiState } from '../search-router/url-handling';
import { savedSearchRelativeHref } from './parse-saved-search-summary';
import { normalizeSavedSearchUrlInput } from './normalize-saved-search-url';
import { canonicalSavedSearchPath } from './compute-saved-search-sync-plan';
import { getRegionTagType } from '../facets/region-tag-labels';
import finlandRegionHierarchy from '../facets/data/finland-region-hierarchy.json';
import type { SearchInsight } from '../../lambda/alerts/search-insights';

export type AppLocale = 'en' | 'fi' | 'sv';

const INDEX_NAME_BY_LOCALE: Record<AppLocale, string> = { en: 'en_search', fi: 'fi_hae', sv: 'sv_sok' };

/** Same locale rule the alert matcher uses (lambda/alerts/build-alert-search-params.ts). */
export function savedSearchLocale(storedUrl: string): AppLocale {
  const path = savedSearchRelativeHref(storedUrl);
  const first = path.split('/').filter(Boolean)[0];
  if (first === 'fi' || first === 'sv' || first === 'en') return first;
  if (path.includes('/tyopaikat') || path.includes('/tyopaikka')) return 'fi';
  if (path.includes('/lediga-jobb') || path.includes('/jobb')) return 'sv';
  return 'en';
}

/** Editable form of a stored search: the SRP's own UI state plus the locale it belongs to. */
export function savedSearchToUiState(storedUrl: string): { state: IndexUiState; locale: AppLocale } {
  const state = parseUrlToIndexUiState(savedSearchRelativeHref(storedUrl));
  return { state, locale: savedSearchLocale(storedUrl) };
}

/** Serialises edited UI state back to a stored-search URL (path + query, page reset to the first). */
export function uiStateToSavedSearchUrl(state: IndexUiState, locale: AppLocale): string {
  const refinementList: Record<string, string[]> = {};
  for (const [attribute, values] of Object.entries(state.refinementList ?? {})) {
    const cleaned = (values ?? []).map((v) => String(v).trim()).filter(Boolean);
    if (cleaned.length > 0) refinementList[attribute] = cleaned;
  }
  const next: IndexUiState = {
    ...state,
    page: 0,
    refinementList: Object.keys(refinementList).length > 0 ? refinementList : undefined,
  };
  delete next.perPage;
  const url = generateUrlFromIndexUiState(next, INDEX_NAME_BY_LOCALE[locale]);
  return normalizeSavedSearchUrlInput(url || `/${locale}`);
}

/** Origin-insensitive: stored searches may be absolute (from the SRP) or relative (built here). */
export function isSameSavedSearchUrl(a: string, b: string): boolean {
  return canonicalSavedSearchPath(a) === canonicalSavedSearchPath(b);
}

const CITY_TO_ADMIN1: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const [admin1, cities] of Object.entries(finlandRegionHierarchy as Record<string, string[]>)) {
    for (const city of cities) map[city] = admin1;
  }
  return map;
})();

/** The maakunta a Finnish city placeId belongs to, when known. */
export function parentRegionForCity(placeId: string): string | undefined {
  return CITY_TO_ADMIN1[placeId];
}

/** Seniority words whose removal is the cheapest way to widen a too-narrow query. */
const QUALIFIER_WORDS = new Set([
  'senior',
  'junior',
  'lead',
  'principal',
  'head',
  'chief',
  'staff',
  'intern',
  'trainee',
  'harjoittelija',
  'vanhempi',
  'nuorempi',
  'johtava',
  'ledande',
  'praktikant',
]);

export type SearchFixKind = 'dropQualifier' | 'widenToRegion' | 'dropFacet' | 'addRegion' | 'sortByDate';

export interface SearchFix {
  kind: SearchFixKind;
  /** Candidate stored-search URL after applying the fix. */
  url: string;
  /** Human detail for the label: the word dropped, the facet attribute removed, the region placeId added or widened to. */
  detail: string;
}

export interface SuggestSearchFixesInput {
  storedUrl: string;
  insight: SearchInsight;
  /** The user's preferred regionTags (profile), used to narrow a too-broad search. */
  profileRegionTags?: string[];
}

/**
 * Concrete, one-tap ways to fix a badly tuned search. Returns candidate URLs only; the caller
 * fetches live counts for them so the UI can promise "widen to Uusimaa: 41 jobs" rather than guess.
 */
export function suggestSearchFixes(input: SuggestSearchFixesInput): SearchFix[] {
  const { state, locale } = savedSearchToUiState(input.storedUrl);
  const fixes: SearchFix[] = [];
  const refinements = state.refinementList ?? {};
  const health = input.insight.health;

  if (health === 'empty' || health === 'stale') {
    const words = (state.query ?? '').trim().split(/\s+/).filter(Boolean);
    const qualifier = words.find((w) => QUALIFIER_WORDS.has(w.toLowerCase()));
    if (qualifier && words.length > 1) {
      const query = words.filter((w) => w !== qualifier).join(' ');
      fixes.push({ kind: 'dropQualifier', detail: qualifier, url: uiStateToSavedSearchUrl({ ...state, query }, locale) });
    }

    const regionTags = refinements.regionTags ?? [];
    const city = regionTags.find((v) => getRegionTagType(v) === 'city' && parentRegionForCity(v));
    if (city) {
      const parent = parentRegionForCity(city)!;
      const nextTags = Array.from(new Set(regionTags.map((v) => (v === city ? parent : v))));
      fixes.push({
        kind: 'widenToRegion',
        detail: parent,
        url: uiStateToSavedSearchUrl({ ...state, refinementList: { ...refinements, regionTags: nextTags } }, locale),
      });
    }

    const droppable = Object.keys(refinements).filter((attr) => attr !== 'regionTags' && attr !== 'location' && (refinements[attr]?.length ?? 0) > 0);
    const last = droppable[droppable.length - 1];
    if (last) {
      const { [last]: _dropped, ...rest } = refinements;
      fixes.push({ kind: 'dropFacet', detail: last, url: uiStateToSavedSearchUrl({ ...state, refinementList: rest }, locale) });
    }
  }

  if (health === 'broad') {
    const hasLocation = (refinements.regionTags?.length ?? 0) > 0 || (refinements.location?.length ?? 0) > 0;
    const region = input.profileRegionTags?.[0];
    if (!hasLocation && region) {
      fixes.push({
        kind: 'addRegion',
        detail: region,
        url: uiStateToSavedSearchUrl({ ...state, refinementList: { ...refinements, regionTags: [region] } }, locale),
      });
    }
    if (state.sort !== 'date') {
      fixes.push({ kind: 'sortByDate', detail: '', url: uiStateToSavedSearchUrl({ ...state, sort: 'date' }, locale) });
    }
  }

  // Never suggest a no-op.
  return fixes.filter((fix) => !isSameSavedSearchUrl(fix.url, input.storedUrl));
}
